// Профиль студента (данные титула по умолчанию, запомненные руководители и дисциплины) - сервер
// /api/sto/profile (свой у каждого пользователя: <данные>\users\<uid>\profile.json) - и настройки браузера.
import { api } from '../../core/api';
import type { StudentData } from '../../core/types';
import { readPref, writePref } from '../../core/store';

export interface Profile {
  student: StudentData;
  teachers: { name: string; position: string }[];
  disciplines: string[];
  saved: boolean;
}

let cache: Profile | null = null;
let pending: Promise<Profile> | null = null;
const listeners = new Set<() => void>();

export function getProfileCached(): Profile | null { return cache; }
export function onProfile(fn: () => void) { listeners.add(fn); return () => { listeners.delete(fn); }; }
function publish(p: Profile) { cache = p; listeners.forEach((f) => f()); return p; }

export function loadProfile(force = false): Promise<Profile> {
  if (cache && !force) return Promise.resolve(cache);
  if (!pending) pending = api.get<Profile>('/api/sto/profile').then(publish).finally(() => { pending = null; });
  return pending;
}

export async function saveProfile(patch: Partial<Profile>): Promise<Profile> {
  return publish(await api.post<Profile>('/api/sto/profile', patch));
}

/** Запомнить руководителя и дисциплину (последние - первыми в подсказках). */
export async function remember(teacher?: { name: string; position: string } | null, discipline?: string) {
  if (!teacher?.name && !discipline) return;
  try { publish(await api.post<Profile>('/api/sto/profile/remember', { teacher, discipline })); } catch { /* не критично */ }
}

// ------------------------------------------------------------------------------------------------ настройки браузера
export const prefs = {
  get typo(): boolean { return readPref('typo', '1') !== '0'; },
  set typo(v: boolean) { writePref('typo', v ? '1' : '0'); },
  get lintInline(): boolean { return readPref('lintInline', '1') !== '0'; },
  set lintInline(v: boolean) { writePref('lintInline', v ? '1' : '0'); },
  phrase(kind: string): string | null { return readPref('phrase.' + kind, null); },
  setPhrase(kind: string, v: string) { writePref('phrase.' + kind, v); },
};

/** Пропущенные («игнорировать») замечания - по проекту, в браузере. Ключ: код|узел|фрагмент. */
export const ignores = {
  /** растёт при каждом изменении - для сброса кэша проверки */
  stamp: 0,
  list(pid: string | null): Set<string> {
    try { return new Set(JSON.parse(readPref('ignore.' + (pid || ''), '[]') || '[]')); } catch { return new Set(); }
  },
  set(pid: string | null, s: Set<string>) { ignores.stamp++; writePref('ignore.' + (pid || ''), JSON.stringify([...s].slice(-500))); },
};
